// OSIS - Control Calitate - Verificarea calității produselor finite
import React, { useState } from 'react';
import { 
  CheckCircle, XCircle, AlertTriangle, Star, Eye, Plus,
  Camera, FileText, Clock, User, Package, Award
} from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const QualityControl = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [isInspectionModalOpen, setIsInspectionModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Inspeccii calitate pentru produsele OSIS
  const qualityInspections = [
    {
      id: 'QC-001',
      orderId: 'OSIS-024',
      product: 'proiect epoxidic 3 camere 120x140',
      inspector: 'Daniel Bărbosu',
      date: '2024-03-16',
      status: 'Aprobat',
      score: 9.5,
      criteria: {
        dimensiuni: { score: 10, notes: 'Dimensiuni perfecte conform specificației' },
        sudura: { score: 9.5, notes: 'Sudură excelentă, unghiuri perfecte' },
        materiale: { score: 9, notes: 'materiale montată corect, funcționare optimă' },
        etansare: { score: 9.5, notes: 'Etanșare perfectă, fără infiltrații' },
        finisaje: { score: 9.5, notes: 'Finisaje impecabile, aspect profesional' }
      },
      defects: [],
      photos: ['qc-001-1.jpg', 'qc-001-2.jpg'],
      approved: true,
      notes: 'Produs de calitate superioară, fără defecte identificate'
    },
    {
      id: 'QC-002',
      orderId: 'OSIS-025',
      product: 'pardoseală heavy duty 90x200',
      inspector: 'Beniamin Cojocariu',
      date: '2024-03-17',
      status: 'Respins',
      score: 6.8,
      criteria: {
        dimensiuni: { score: 9, notes: 'Dimensiuni corecte' },
        sudura: { score: 8, notes: 'Sudură bună dar cu mici neregularități' },
        materiale: { score: 5, notes: 'Problemă la încuietoarea principală' },
        etansare: { score: 7, notes: 'Etanșare acceptabilă' },
        finisaje: { score: 6, notes: 'Zgârieturi minore pe suprafață' }
      },
      defects: [
        'Încuietoare principală nu funcționează optim',
        'Zgârieturi pe partea frontală',
        'Mâner ușor deplasat'
      ],
      photos: ['qc-002-1.jpg', 'qc-002-2.jpg', 'qc-002-defect.jpg'],
      approved: false,
      notes: 'Necesită refacerea materialei și repararea finisajelor'
    },
    {
      id: 'QC-003',
      orderId: 'OSIS-026',
      product: 'proiect epoxidic 3 camere 150x160',
      inspector: 'Romică Rumega',
      date: '2024-03-18',
      status: 'În așteptare',
      score: null,
      criteria: {},
      defects: [],
      photos: [],
      approved: null,
      notes: 'Programat pentru inspecție mâine dimineața'
    },
    {
      id: 'QC-004',
      orderId: 'OSIS-027',
      product: 'suprafață mare 200x240',
      inspector: 'Daniel Bărbosu',
      date: '2024-03-15',
      status: 'Aprobat cu rezerve',
      score: 8.2,
      criteria: {
        dimensiuni: { score: 10, notes: 'Dimensiuni exacte' },
        sudura: { score: 9, notes: 'Sudură foarte bună' },
        materiale: { score: 7, notes: 'materiale funcțională cu ajustări minore' },
        etansare: { score: 8, notes: 'Etanșare bună' },
        finisaje: { score: 8, notes: 'Finisaje corecte' }
      },
      defects: [
        'Ajustări necesare la materiale pentru optimizare'
      ],
      photos: ['qc-004-1.jpg'],
      approved: true,
      notes: 'Aprobat după efectuarea ajustărilor la materiale'
    }
  ];

  // Statistici calitate
  const stats = {
    totalInspections: qualityInspections.length,
    approved: qualityInspections.filter(i => i.status === 'Aprobat').length,
    rejected: qualityInspections.filter(i => i.status === 'Respins').length,
    pending: qualityInspections.filter(i => i.status === 'În așteptare').length,
    averageScore: qualityInspections
      .filter(i => i.score !== null)
      .reduce((sum, i) => sum + i.score, 0) / 
      qualityInspections.filter(i => i.score !== null).length
  };

  // Filtrare inspecții
  const filteredInspections = qualityInspections.filter(inspection => {
    if (selectedFilter === 'all') return true;
    return inspection.status === selectedFilter;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'Aprobat': return 'success';
      case 'Respins': return 'error';
      case 'Aprobat cu rezerve': return 'warning';
      case 'În așteptare': return 'info';
      default: return 'gray';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 9) return 'text-green-600';
    if (score >= 7.5) return 'text-yellow-600';
    if (score >= 6) return 'text-orange-600';
    return 'text-red-600';
  };

  const handleViewDetails = (inspection) => {
    setSelectedProduct(inspection);
    setIsInspectionModalOpen(true);
  };

  const renderStars = (score) => {
    const stars = Math.floor(score / 2);
    const hasHalfStar = (score % 2) >= 1;
    
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= stars
                ? 'text-yellow-400 fill-current'
                : star === stars + 1 && hasHalfStar
                ? 'text-yellow-400 fill-current opacity-50'
                : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-1 text-sm font-medium">{score?.toFixed(1)}</span>
      </div>
    );
  };

  const tableColumns = [
    {
      header: 'Inspecție',
      accessor: 'id',
      cell: (row) => (
        <div>
          <p className="font-semibold text-gray-900 dark:text-white">{row.id}</p>
          <p className="text-sm text-gray-500">{row.orderId}</p>
        </div>
      ),
    },
    {
      header: 'Produs',
      accessor: 'product',
      cell: (row) => (
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-gray-400" />
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{row.product}</p>
            <p className="text-xs text-gray-500">
              {new Date(row.date).toLocaleDateString('ro-RO')}
            </p>
          </div>
        </div>
      ),
    },
    {
      header: 'Inspector',
      accessor: 'inspector',
      cell: (row) => (
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-gray-400" />
          <span className="text-sm">{row.inspector}</span>
        </div>
      ),
    },
    {
      header: 'Status',
      accessor: 'status',
      cell: (row) => (
        <Badge variant={getStatusColor(row.status)}>
          {row.status}
        </Badge>
      ),
    },
    {
      header: 'Scor Calitate',
      accessor: 'score',
      cell: (row) => (
        <div className="text-center">
          {row.score !== null ? (
            <>
              <div className={`text-lg font-bold ${getScoreColor(row.score)}`}>
                {row.score}/10
              </div>
              {renderStars(row.score)}
            </>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </div>
      ),
    },
    {
      header: 'Defecte',
      accessor: 'defects',
      cell: (row) => (
        <div className="text-center">
          {row.defects.length > 0 ? (
            <Badge variant="error">{row.defects.length} defecte</Badge>
          ) : row.status === 'În așteptare' ? (
            <span className="text-gray-400">-</span>
          ) : (
            <Badge variant="success">0 defecte</Badge>
          )}
        </div>
      ),
    },
    {
      header: 'Acțiuni',
      accessor: 'actions',
      cell: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleViewDetails(row)}
            className="btn-ghost btn-sm"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button className="btn-ghost btn-sm">
            <FileText className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <CheckCircle className="w-8 h-8 text-primary-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Control Calitate OSIS
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Verificarea calității produselor finite - pardoseli industriale
            </p>
          </div>
        </div>
        
        <button className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Inspecție Nouă
        </button>
      </div>

      {/* Statistici */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Inspecții</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalInspections}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-primary-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Aprobate</p>
              <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
              <p className="text-xs text-gray-500">
                {Math.round((stats.approved / stats.totalInspections) * 100)}% din total
              </p>
            </div>
            <Award className="w-8 h-8 text-green-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Respinse</p>
              <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
              <p className="text-xs text-gray-500">
                {Math.round((stats.rejected / stats.totalInspections) * 100)}% din total
              </p>
            </div>
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">În Așteptare</p>
              <p className="text-2xl font-bold text-blue-600">{stats.pending}</p>
            </div>
            <Clock className="w-8 h-8 text-blue-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Scor Mediu</p>
              <p className={`text-2xl font-bold ${getScoreColor(stats.averageScore)}`}>
                {stats.averageScore.toFixed(1)}/10
              </p>
              {renderStars(stats.averageScore)}
            </div>
            <Star className="w-8 h-8 text-yellow-500" />
          </div>
        </Card>
      </div>

      {/* Filtre */}
      <Card>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => setSelectedFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedFilter === 'all'
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            Toate ({stats.totalInspections})
          </button>
          <button
            onClick={() => setSelectedFilter('Aprobat')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedFilter === 'Aprobat'
                ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            Aprobate ({stats.approved})
          </button>
          <button
            onClick={() => setSelectedFilter('Respins')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedFilter === 'Respins'
                ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            Respinse ({stats.rejected})
          </button>
          <button
            onClick={() => setSelectedFilter('În așteptare')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedFilter === 'În așteptare'
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            În Așteptare ({stats.pending})
          </button>
        </div>
      </Card>

      {/* Tabel Inspecții */}
      <Card title="Inspecții Calitate" icon={CheckCircle}>
        <div className="overflow-x-auto">
          <Table 
            columns={tableColumns} 
            data={filteredInspections}
          />
        </div>
      </Card>

      {/* Modal Detalii Inspecție */}
      <Modal
        isOpen={isInspectionModalOpen}
        onClose={() => setIsInspectionModalOpen(false)}
        title={`Detalii Inspecție ${selectedProduct?.id || ''}`}
        size="lg"
      >
        {selectedProduct && (
          <div className="space-y-6">
            {/* Header inspecție */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Informații Generale
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">ID Inspecție:</span>
                    <span className="font-semibold">{selectedProduct.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">ID Comandă:</span>
                    <span className="font-semibold">{selectedProduct.orderId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Produs:</span>
                    <span className="font-semibold">{selectedProduct.product}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Inspector:</span>
                    <span className="font-semibold">{selectedProduct.inspector}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Data:</span>
                    <span className="font-semibold">
                      {new Date(selectedProduct.date).toLocaleDateString('ro-RO')}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Rezultat Inspecție
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Status:</span>
                    <Badge variant={getStatusColor(selectedProduct.status)}>
                      {selectedProduct.status}
                    </Badge>
                  </div>
                  {selectedProduct.score !== null && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Scor:</span>
                        <span className={`text-xl font-bold ${getScoreColor(selectedProduct.score)}`}>
                          {selectedProduct.score}/10
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Rating:</span>
                        {renderStars(selectedProduct.score)}
                      </div>
                    </>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Defecte:</span>
                    <span className={`font-semibold ${
                      selectedProduct.defects.length > 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {selectedProduct.defects.length} identificate
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Criterii evaluare */}
            {Object.keys(selectedProduct.criteria).length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Criterii de Evaluare
                </h3>
                <div className="space-y-4">
                  {Object.entries(selectedProduct.criteria).map(([criterion, data]) => (
                    <div key={criterion} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium capitalize">{criterion}</span>
                        <span className={`font-bold ${getScoreColor(data.score)}`}>
                          {data.score}/10
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {data.notes}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Defecte identificate */}
            {selectedProduct.defects.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Defecte Identificate
                </h3>
                <div className="space-y-2">
                  {selectedProduct.defects.map((defect, index) => (
                    <div key={index} className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                      <span className="text-red-800 dark:text-red-200">{defect}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Observații */}
            {selectedProduct.notes && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Observații Inspector
                </h3>
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-blue-800 dark:text-blue-200">
                    {selectedProduct.notes}
                  </p>
                </div>
              </div>
            )}

            {/* Fotografii */}
            {selectedProduct.photos.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Fotografii Inspecție
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {selectedProduct.photos.map((photo, index) => (
                    <div key={index} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                      <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 dark:text-gray-400">{photo}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                onClick={() => setIsInspectionModalOpen(false)}
                className="btn-secondary"
              >
                Închide
              </button>
              <button className="btn-primary flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Export Raport
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default QualityControl;